'use client';

import { PageProps } from '@/lib/types';
import { createRandomId } from '@/lib/utils';
import { Breadcrumb, Empty, Section } from '@/ui';
import { Content, PaginationWidget } from '@/widgets';
import { Container, Flex, Select, Skeleton, Table } from '@mantine/core';
import { useTranslations } from 'next-intl';
import { useParams, useSearchParams } from 'next/navigation';
import { useState } from 'react';
import useSWR from 'swr';

type TReestrData = {
  id: number;
  institutionName: string;
  fullName: string;
  certificate: string;
  dateBeginExamDay: string;
  dateEndAttestationCert: string;
};

type TData = {
  data: {
    totalPage: number;
    current_page: string;
    data: TReestrData[];
  };
};

type Props = {
  // data: PageProps & TData;
  // children: React.ReactNode;
};

export default function ReestrAtt({}: Props) {
  const t = useTranslations();
  const searchParams = useSearchParams();
  const { news_code } = useParams<{ news_code: string }>();
  const [perPage, setPerPage] = useState<string | null>('20');

  const { data, isLoading } = useSWR<PageProps & TData>([
    '/authorities/argos/get-reestr',
    { params: { code: news_code, per_page: perPage, page: searchParams.get('page') } },
  ]);

  const breadcrumbItems = [
    { title: data?.breadcrumb, href: `/general-menu/${data?.menu_id}` },
    { title: data?.menu_item_title, isCurrent: true },
  ];

  return (
    <Container>
      <Section rightMenu={{ isActive: true, id: data?.menu_id, activeItem: { id: data?.menu_item_id || 0 } }}>
        <Content>
          <Content.Header>
            <Breadcrumb items={breadcrumbItems} />
          </Content.Header>
          <Content.Title>{data?.menu_item_title}</Content.Title>
          <Content.Body>
            <Table.ScrollContainer minWidth={500} pt="lg">
              <Table
                highlightOnHover
                withColumnBorders
                striped
                horizontalSpacing="md"
                verticalSpacing="md"
                bg="#eef4fe"
                borderColor="#bed2eb"
                style={{ borderRadius: '25px' }}
              >
                <Table.Thead>
                  <Table.Tr>
                    <Table.Td rowSpan={2} align="center">
                      {t('t-r')}
                    </Table.Td>
                    <Table.Td rowSpan={2} align="center">
                      {t('tashkilot-nomi')}
                    </Table.Td>
                    <Table.Td rowSpan={2} align="center">
                      {t('f-i-sh')}
                    </Table.Td>
                    <Table.Td colSpan={3} align="center">
                      {t('attestatsiya')}
                    </Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td align="center">{t('raqami')}</Table.Td>
                    <Table.Td align="center">{t('o-tgan-sana')}</Table.Td>
                    <Table.Td align="center">{t('amal-qilish-muddati')}</Table.Td>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {isLoading ? (
                    Array.from({ length: 5 }, () => (
                      <Table.Tr key={createRandomId()}>
                        <Table.Td>
                          <Skeleton width="100%" height="3rem" bg="#f9f9f9" radius="0" />
                        </Table.Td>
                        <Table.Td>
                          <Skeleton width="100%" height="3rem" bg="#f9f9f9" radius="0" />
                        </Table.Td>
                        <Table.Td>
                          <Skeleton width="100%" height="3rem" bg="#f9f9f9" radius="0" />
                        </Table.Td>
                        <Table.Td>
                          <Skeleton width="100%" height="3rem" bg="#f9f9f9" radius="0" />
                        </Table.Td>
                        <Table.Td>
                          <Skeleton width="100%" height="3rem" bg="#f9f9f9" radius="0" />
                        </Table.Td>
                        <Table.Td>
                          <Skeleton width="100%" height="3rem" bg="#f9f9f9" radius="0" />
                        </Table.Td>
                      </Table.Tr>
                    ))
                  ) : data && data?.data && data?.data?.data?.length > 0 ? (
                    data?.data?.data?.map((item, index) => (
                      <Table.Tr key={item.id}>
                        <Table.Td align="center">
                          {(parseInt(searchParams.get('page') ?? '1', 10) - 1) * parseInt(perPage ?? '20', 10) +
                            index +
                            1}
                        </Table.Td>
                        <Table.Td>{item.institutionName}</Table.Td>
                        <Table.Td>{item.fullName}</Table.Td>
                        <Table.Td>{item.certificate}</Table.Td>
                        <Table.Td>{item.dateBeginExamDay}</Table.Td>
                        <Table.Td>{item.dateEndAttestationCert}</Table.Td>
                      </Table.Tr>
                    ))
                  ) : (
                    <Empty />
                  )}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
            <Flex align="center" justify="center">
              <PaginationWidget totalPage={data?.data?.totalPage} />
              <Select
                className="w-2/12"
                label={t('ko-rsatish')}
                value={perPage}
                onChange={setPerPage}
                data={[
                  { label: '10', value: '10' },
                  { label: '15', value: '15' },
                  { label: '20', value: '20' },
                  { label: '25', value: '25' },
                ]}
              />
            </Flex>
          </Content.Body>
        </Content>
      </Section>
    </Container>
  );
}
